import React, { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { useTranslation } from '../../providers/TranslationProvider';

interface GoogleMapContextValue {
	isLoaded: boolean;
	loadError: string | null;
	google: typeof window.google | null;
}

const GoogleMapContext = createContext<GoogleMapContextValue | undefined>(undefined);

export interface GoogleMapProviderProps {
	children: ReactNode;
	apiKey: string;
	libraries?: Array<'places' | 'geometry' | 'drawing' | 'visualization' | 'marker'>;
}

export const GoogleMapProvider: React.FC<GoogleMapProviderProps> = ({
	children,
	apiKey,
	libraries = ['places', 'geometry', 'marker'],
}) => {
	const { t } = useTranslation();
	const [isLoaded, setIsLoaded] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [google, setGoogle] = useState<typeof window.google | null>(null);

	useEffect(() => {
		const loader = new Loader({
			apiKey,
			version: 'weekly',
			libraries,
		});

		loader
			.load()
			.then(() => {
				if (window.google != null) {
					setGoogle(window.google);
					setIsLoaded(true);
				}
			})
			.catch((error: Error) => {
				setLoadError(error.message ?? t('Failed to load Google Maps'));
				setIsLoaded(false);
			});
	}, [apiKey, libraries, t]);

	const value: GoogleMapContextValue = {
		isLoaded,
		loadError,
		google,
	};

	return (
		<GoogleMapContext.Provider value={value}>
			{children}
		</GoogleMapContext.Provider>
	);
};

export const useGoogleMap = (): GoogleMapContextValue => {
	const context = useContext(GoogleMapContext);

	if (context === undefined) {
		throw new Error('useGoogleMap must be used within a GoogleMapProvider');
	}

	return context;
};
